import { Link } from 'react-router-dom';
import { ArrowRight, Quote, CheckCircle, Target, Eye, Heart, Users, Lightbulb, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const IMAGES = {
  about: "https://images.unsplash.com/photo-1486892539609-d5322f938c50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwY29tbXVuaXR5JTIweW91dGglMjB3b3Jrc2hvcCUyMGNyZWF0aXZlJTIwbG9uZG9ufGVufDB8fHx8MTc3MzA0Mzc0OXww&ixlib=rb-4.1.0&q=85",
};

const values = [
  {
    icon: Heart,
    title: 'Community First',
    description: 'Every decision we make starts with the question: "How does this serve our communities?"',
  },
  {
    icon: Users,
    title: 'Inclusivity',
    description: 'We create spaces where everyone belongs, regardless of background, age, or circumstance.',
  },
  {
    icon: Lightbulb,
    title: 'Creative Impact',
    description: 'We use creativity as a tool for change, delivering projects that inspire and engage.',
  },
  {
    icon: Shield,
    title: 'Safety & Trust',
    description: 'We prioritise creating environments where people feel safe to be themselves.',
  },
  {
    icon: Target,
    title: 'Accountability',
    description: 'We measure our impact and hold ourselves accountable to the communities we serve.',
  },
  {
    icon: Eye,
    title: 'Transparency',
    description: 'We operate with openness and honesty in all our partnerships and projects.',
  },
];

const whatWeDo = [
  'Design and deliver community events that bring people together',
  'Create youth-focused programmes that build confidence and skills',
  'Develop safe spaces for dialogue, creativity, and connection',
  'Build partnerships with brands and organisations committed to social impact',
  'Provide insight reports that demonstrate real community value',
  'Support families and young people through accessible programming',
];

export const AboutPage = () => {
  return (
    <main data-testid="about-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-medium tracking-wide uppercase text-[#E3B505] mb-4 block">About Us</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#2D2A26] mb-6 font-['Manrope']">
              Building Stronger Communities, Together
            </h1>
            <p className="text-lg md:text-xl text-[#5C5852] leading-relaxed">
              OVYC is a London-based community project management organisation dedicated to creating 
              safe spaces, delivering impactful programmes, and building partnerships that make a 
              real difference in people's lives.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-semibold text-[#2D2A26] font-['Manrope']">
                Who We Are
              </h2>
              <div className="space-y-4 text-[#5C5852] text-lg leading-relaxed">
                <p>
                  Our Village, Your Community (OVYC) was born from a simple belief: that strong 
                  communities are built through connection, support, and shared experiences. We 
                  are project managers, creatives, and community advocates working to rebuild the 
                  spaces and programmes that help people thrive.
                </p>
                <p>
                  Based in London, we work with local communities, brands, venues, and organisations 
                  to deliver events, youth programmes, and initiatives that create real, measurable 
                  impact. Our approach is always community-led because we know that lasting change 
                  comes from working with people, not for them.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src={IMAGES.about}
                alt="OVYC community event"
                className="w-full h-[400px] md:h-[500px] object-cover rounded-3xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#E3B505]/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-28 bg-[#F4F1DE]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Mission */}
            <div 
              data-testid="mission-card"
              className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-[#E6E2D8]/50"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#E3B505] flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-[#2D2A26]" />
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-[#2D2A26] mb-4 font-['Manrope']">
                Our Mission
              </h3>
              <p className="text-[#5C5852] text-lg leading-relaxed">
                To rebuild community connection by creating safe spaces, delivering youth-focused 
                programmes, and building partnerships that translate into real support for the 
                people and places that need it most. We exist to make community support feel 
                accessible, meaningful, and real.
              </p>
            </div>

            {/* Vision */}
            <div 
              data-testid="vision-card"
              className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-[#E6E2D8]/50"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#264653] flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-[#2D2A26] mb-4 font-['Manrope']">
                Our Vision
              </h3>
              <p className="text-[#5C5852] text-lg leading-relaxed">
                A future where every community has access to the spaces, programmes, and support 
                networks they need to thrive. Where young people grow up with opportunities to 
                build confidence and belonging. Where brands and organisations see community 
                investment as essential, not optional.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#E76F51] mb-4 block">Our Work</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#2D2A26] font-['Manrope']">
              What OVYC Does
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid sm:grid-cols-2 gap-4">
              {whatWeDo.map((item, index) => (
                <div 
                  key={index}
                  data-testid={`what-we-do-${index}`}
                  className="flex items-start gap-4 p-4 rounded-2xl hover:bg-[#F4F1DE] transition-colors"
                >
                  <CheckCircle className="w-6 h-6 text-[#E3B505] flex-shrink-0 mt-0.5" />
                  <span className="text-[#2D2A26] text-base leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section 
        data-testid="values-section"
        className="py-20 md:py-28 bg-[#FAF9F6]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-wide uppercase text-[#264653] mb-4 block">What Guides Us</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#2D2A26] font-['Manrope']">
              Our Core Values
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div 
                key={value.title}
                data-testid={`value-card-${index}`}
                className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-md transition-all duration-300 border border-[#E6E2D8]/50"
              >
                <div className="w-12 h-12 rounded-xl bg-[#E3B505]/10 flex items-center justify-center mb-5">
                  <value.icon className="w-6 h-6 text-[#E3B505]" />
                </div>
                <h3 className="text-xl font-semibold text-[#2D2A26] mb-2 font-['Manrope']">{value.title}</h3>
                <p className="text-[#5C5852] leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section 
        data-testid="founder-section"
        className="py-20 md:py-28 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-[#F4F1DE] rounded-3xl p-8 md:p-12 relative">
              <Quote className="w-12 h-12 text-[#E3B505]/30 absolute top-8 left-8" />
              <div className="relative z-10 text-center">
                <span className="text-sm font-medium tracking-wide uppercase text-[#E3B505] mb-6 block">
                  Our Story
                </span>
                <p className="text-xl md:text-2xl text-[#2D2A26] leading-relaxed font-['Manrope'] font-medium">
                  "OVYC was founded by a community project manager driven by a clear mission: 
                  to bring back safe, supportive spaces for young people and families. After 
                  seeing the gaps in community support, youth provision and real connection, 
                  she created OVYC to lead projects that make people feel seen, supported and 
                  part of something bigger."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#264653]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6 font-['Manrope']">
            Want to Work With Us?
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Whether you're a brand, organisation, venue, or funder looking to create real community 
            impact, we'd love to explore how we can work together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/partner">
              <Button 
                data-testid="about-partner-btn"
                className="bg-[#E3B505] text-[#2D2A26] hover:bg-[#E3B505]/90 rounded-full h-14 px-10 text-base font-medium shadow-lg"
              >
                Explore Partnership
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                data-testid="about-contact-btn"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 rounded-full h-14 px-10 text-base font-medium"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
